import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Heart, ArrowRight, Package } from 'lucide-react';
import ProductCard from '@/components/ProductCard';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useApp } from '@/context/AppContext';

const WishlistPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { API } = useApp();

  useEffect(() => {
    const fetchWishlist = async () => {
      setLoading(true);
      try {
        const savedIds = JSON.parse(localStorage.getItem('gpgt_wishlist') || '[]');
        if (savedIds.length === 0) {
          setProducts([]);
          setLoading(false);
          return;
        }

        // Fetch products one by one (since we don't have a public bulk endpoint)
        const promises = savedIds.map(id => axios.get(`${API}/products/${id}`));
        const responses = await Promise.allSettled(promises);
        
        const wishlistProducts = [];
        responses.forEach(res => {
          if (res.status === 'fulfilled' && res.value.data) {
            wishlistProducts.push(res.value.data);
          }
        });
        
        setProducts(wishlistProducts);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load wishlist products');
      }
      setLoading(false);
    };

    fetchWishlist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API]);

  const handleClearWishlist = () => {
    localStorage.removeItem('gpgt_wishlist');
    setProducts([]);
    toast('Wishlist cleared');
  };

  return (
    <div className="min-h-screen bg-gray-50/50 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#0c1b44] flex items-center gap-3">
              <Heart className="h-8 w-8 text-rose-500 fill-rose-500" />
              My Wishlist
            </h1>
            <p className="text-gray-500 mt-2">
              {products.length} {products.length === 1 ? 'item' : 'items'} saved for later
            </p>
          </div>
          {products.length > 0 && (
            <Button 
              variant="outline" 
              onClick={handleClearWishlist}
              className="text-red-500 hover:text-red-600 hover:bg-red-50 border-red-200"
            >
              Clear Wishlist
            </Button>
          )}
        </div>

        {/* Content */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-80 bg-white rounded-xl border border-gray-100 animate-pulse">
                <div className="w-full h-48 bg-gray-200 rounded-t-xl" />
                <div className="p-4 space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-4 bg-gray-200 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center shadow-sm">
            <div className="w-24 h-24 bg-rose-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <Heart className="h-10 w-10 text-rose-300" />
            </div>
            <h2 className="text-2xl font-bold text-navy mb-3">Your wishlist is empty</h2>
            <p className="text-gray-500 max-w-md mx-auto mb-8">
              Looks like you haven't saved any items yet. Start exploring our catalog to find products you love!
            </p>
            <Link to="/products">
              <Button className="bg-[#0c1b44] hover:bg-[#1a2d64] text-white px-8 py-6 rounded-xl text-lg h-auto transition-all shadow-lg shadow-navy/20">
                <Package className="mr-2 h-5 w-5" /> Browse Products <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {products.map((product, index) => (
              <ProductCard 
                key={product.id} 
                product={product} 
                index={index} 
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WishlistPage;
